// Runtime configuration, resolved once at module load.
//
// window.__ENV__ is written by docker-entrypoint.sh at container start from the
// k8s deployment env, so ONE image serves beta1 and staging. process.env is the
// Parcel build-time fallback for local dev.

const runtime = (typeof window !== 'undefined' && window.__ENV__) || {};

/** Admin BFF origin. The GraphQL endpoint is this root — the BFF serves it at /. */
export const ADMIN_BFF_URL =
  runtime.ADMIN_BFF_URL || process.env.ADMIN_BFF_URL || 'http://localhost:4003';

// Keycloak origin, no realm path — auth.js appends /realms/<realm>. This used to
// be a bare constant in auth.js, which meant moving SSO host required editing and
// rebuilding the client; routing it through here lets the deployment decide.
// KEYCLOAK_URL is the brand-neutral name and wins; PARSINA_KC_BASE_URL is the one
// every existing deployment sets, and it keeps working.
export const KC_BASE_URL =
  runtime.KEYCLOAK_URL ||
  runtime.PARSINA_KC_BASE_URL ||
  process.env.PARSINA_KC_BASE_URL ||
  'https://sso.itechtrade.ir';

// ── Keycloak identity: realm and client ─────────────────────────────────────
// Which realm this panel signs in to and which client it signs in as. Parsina's
// own values are the fallbacks, so an image run with none of these set signs in
// exactly as before; a deployment on another realm (Proxia) sets them at
// container start, same image. The same lookups as the doctor panel's.
//
// Each lookup takes the first value that is set: the brand-neutral name in
// env.js, the legacy PARSINA_* name in env.js, what the image was built with,
// today's literal. docker-entrypoint.sh writes the neutral keys and
// PARSINA_KC_REALM only when they are set.
//
// Read on every call, not once at load like the values above: what decides is
// the env.js of the page that is running, and a test can set its own.
const runtimeNow = () => (typeof window !== 'undefined' && window.__ENV__) || {};

/** The realm: the path segment after /realms/. */
export function kcRealm() {
  const env = runtimeNow();
  return env.KEYCLOAK_REALM || env.PARSINA_KC_REALM
    || process.env.KEYCLOAK_REALM || process.env.PARSINA_KC_REALM
    || 'parsina';
}

/**
 * The OIDC client this panel signs in as (shared with the staff panels).
 *
 * The entrypoint always writes PARSINA_KC_CLIENT_ID (default `parsina-web`), so a
 * deployment either sets that, as every existing one does, or the neutral
 * KEYCLOAK_CLIENT_ID, which wins. There is deliberately no build-time
 * KEYCLOAK_CLIENT_ID: Parcel inlines the whole build environment, and in a BFF's
 * environment that name means the roles client.
 */
export function kcClientId() {
  const env = runtimeNow();
  return env.KEYCLOAK_CLIENT_ID || env.PARSINA_KC_CLIENT_ID
    || process.env.PARSINA_KC_CLIENT_ID
    || 'parsina-web';
}

/**
 * Which deployment this is, shown at the foot of the rail.
 *
 * An ops panel that looks identical on beta1 and on staging is one where an
 * operator eventually suspends a clinic in the wrong estate. The panel cannot
 * infer this — both environments serve the same image from the same bundle — so
 * it has to be told, and being told nothing is honestly reported as `local`
 * rather than guessed at.
 */
export const ENV_NAME =
  runtime.PARSINA_ENV || process.env.PARSINA_ENV || 'local';

/**
 * The client's own version, so a bug report names a build.
 *
 * Parcel inlines `process.env.*` at build time; absent an injected value this
 * reports `dev`, which is what a local `npm start` genuinely is.
 */
/**
 * Where doctors' public pages live — the queue links a doctor-page request to
 * `${DOCTORSITE_PUBLIC_URL}/<slug>`. beta1 serves https://beta1.parsinabot.ir/doctors.
 */
export const DOCTORSITE_PUBLIC_URL =
  runtime.DOCTORSITE_PUBLIC_URL || process.env.DOCTORSITE_PUBLIC_URL || 'https://parsinabot.ir/doctors';

/** The same, as a sentence names it: `parsinabot.ir/doctors` — no scheme, no trailing slash. */
export const DOCTORSITE_LABEL = DOCTORSITE_PUBLIC_URL.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '').replace(/\/+$/, '');

export const APP_VERSION =
  runtime.PARSINA_VERSION || process.env.PARSINA_VERSION || 'dev';
